import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function PrivacyPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      <section className="pt-32 pb-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">Privacybeleid</h1>
          <p className="text-lg text-slate-600 mb-8">Laatst bijgewerkt: januari 2024</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">1. Inleiding</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Utrecht Airport Taxi respecteert uw privacy en behandelt uw persoonsgegevens zorgvuldig. 
                In dit privacybeleid leggen wij uit welke persoonsgegevens wij verzamelen, waarom wij deze 
                verzamelen en hoe wij deze gebruiken.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Dit privacybeleid is van toepassing op alle diensten van Utrecht Airport Taxi en voldoet 
                aan de Algemene Verordening Gegevensbescherming (AVG).
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">2. Welke gegevens verzamelen wij?</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Wij verzamelen de volgende persoonsgegevens wanneer u een rit boekt:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>Voor- en achternaam</li>
                <li>Telefoonnummer</li>
                <li>E-mailadres</li>
                <li>Ophaal- en bestemmingsadres</li>
                <li>Datum en tijd van de rit</li>
                <li>Vluchtnummer (indien van toepassing)</li>
                <li>Aantal passagiers</li>
                <li>Bijzondere verzoeken (kinderzitje, extra bagage, etc.)</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">3. Waarvoor gebruiken wij uw gegevens?</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Wij gebruiken uw persoonsgegevens voor de volgende doeleinden:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>Het uitvoeren van de door u geboekte taxirit</li>
                <li>Communicatie over uw boeking (bevestiging, wijzigingen, herinneringen)</li>
                <li>Vluchttracking bij ophalen vanaf luchthaven</li>
                <li>Facturering en betalingsverwerking</li>
                <li>Klantenservice en afhandeling van vragen of klachten</li>
                <li>Verbetering van onze dienstverlening</li>
                <li>Naleving van wettelijke verplichtingen</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">4. Bewaartermijn</h2>
              <p className="text-slate-700 leading-relaxed">
                Wij bewaren uw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor 
                zij zijn verzameld. Boekingsgegevens worden bewaard voor de duur van 7 jaar conform de 
                fiscale wetgeving. Na deze periode worden uw gegevens verwijderd of geanonimiseerd.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">5. Delen met derden</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Wij delen uw persoonsgegevens alleen met derden wanneer dit noodzakelijk is voor de 
                uitvoering van onze diensten:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>Onze chauffeurs (alleen de gegevens die nodig zijn voor de rit)</li>
                <li>Betalingsproviders (voor het verwerken van betalingen)</li>
                <li>Vluchttracking diensten (voor het volgen van vluchten)</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Wij verkopen uw gegevens nooit aan derden en delen deze alleen met uw toestemming of 
                wanneer wij hiertoe wettelijk verplicht zijn.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">6. Beveiliging</h2>
              <p className="text-slate-700 leading-relaxed">
                Wij nemen passende technische en organisatorische maatregelen om uw persoonsgegevens te 
                beschermen tegen verlies, misbruik, ongeautoriseerde toegang, openbaarmaking, wijziging 
                of vernietiging. Onze website maakt gebruik van SSL-encryptie en alle gegevens worden 
                veilig opgeslagen.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">7. Uw rechten</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                U heeft de volgende rechten met betrekking tot uw persoonsgegevens:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>Recht op inzage: u kunt opvragen welke gegevens wij van u hebben</li>
                <li>Recht op rectificatie: u kunt onjuiste gegevens laten corrigeren</li>
                <li>Recht op verwijdering: u kunt verzoeken uw gegevens te verwijderen</li>
                <li>Recht op beperking: u kunt verzoeken de verwerking te beperken</li>
                <li>Recht op dataportabiliteit: u kunt uw gegevens in een gestructureerd formaat ontvangen</li>
                <li>Recht van bezwaar: u kunt bezwaar maken tegen de verwerking</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Om gebruik te maken van deze rechten, kunt u contact met ons opnemen via de onderstaande 
                contactgegevens.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">8. Cookies</h2>
              <p className="text-slate-700 leading-relaxed">
                Onze website maakt gebruik van functionele cookies die noodzakelijk zijn voor het goed 
                functioneren van de website. Wij gebruiken geen tracking cookies of cookies van derden 
                zonder uw toestemming. Zie ons cookiebeleid voor meer informatie.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">9. Wijzigingen</h2>
              <p className="text-slate-700 leading-relaxed">
                Wij kunnen dit privacybeleid van tijd tot tijd aanpassen. De meest recente versie is 
                altijd te vinden op onze website. Wij raden u aan dit privacybeleid regelmatig te 
                raadplegen.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">10. Contact</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Voor vragen over dit privacybeleid of over de verwerking van uw persoonsgegevens kunt 
                u contact met ons opnemen:
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-slate-900 font-semibold mb-2">Utrecht Airport Taxi</p>
                <p className="text-slate-700">E-mail: privacy@utrechtairporttaxi.nl</p>
                <p className="text-slate-700">Telefoon: 06 1234 5678</p>
                <p className="text-slate-700">Adres: Utrecht, Nederland</p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
